import { projectSettings } from '@redneckz/wildless-cms-uni-blocks';
import { Pagination } from '@redneckz/wildless-cms-uni-blocks/lib/ui-kit/Pagination/Pagination';
import { graphql, type HeadProps, type PageProps } from 'gatsby';
import React, { useMemo } from 'react';
import { Meta } from '../layouts/Head';
import { JournalistInfo } from './JournalistInfo';
import { mainMeta, premiumUltraMeta } from './metaData';
import { NewsSection } from './NewsSection';
import { usePageChanger } from './usePageChanger';

type PageContext = {
  totalPages: number;
  currentPage: number;
  category?: string;
  directions?: [string];
  isFilial?: [boolean | null];
  isPromotion?: [boolean | null];
  isPremiumUltra?: boolean;
  startDate?: string;
};

const NewsList = ({ data, pageContext }: PageProps<Queries.Query, PageContext>) => {
  const { totalPages, currentPage, directions, category, isFilial, isPromotion, startDate } =
    pageContext;
  const date = useMemo(() => {
    if (startDate) {
      const [year, month] = (startDate || '')?.split('-');

      return `${year}-${month}`;
    } else {
      return '';
    }
  }, [startDate]);
  const handlePageChange = usePageChanger({
    directions,
    isPromotion,
    isFilial,
    category,
    date,
  });

  return (
    <section className="@container font-sans group box-border overflow-hidden col-span-12 scroll-mt-12">
      <NewsSection data={data} directions={directions} />
      {totalPages > 1 && (
        <div className="flex justify-center items-center bg-white w-full pb-3xl mt-2xs">
          <Pagination
            currentPage={currentPage}
            onPageChange={handlePageChange}
            totalPages={totalPages}
          />
        </div>
      )}
      <JournalistInfo className="sm:hidden w-full mt-2xs col-span-12 p-6 bg-white grow" />
    </section>
  );
};

export default NewsList;

export const query = graphql`
  query NewsList(
    $productSlug: String
    $skippedNews: Int!
    $newsPerPage: Int!
    $category: [String]
    $directions: [String]
    $isPromotion: [Boolean]
    $startDate: Date
    $endDate: Date
  ) {
    site {
      siteMetadata {
        siteUrl
      }
    }
    allPortalResourcesJson(
      filter: { items: { elemMatch: { key: { ne: null }, value: { ne: null } } } }
    ) {
      nodes {
        items {
          key
          value
        }
      }
    }
    allContentJson(
      sort: { newsDate: DESC }
      limit: $newsPerPage
      skip: $skippedNews
      filter: {
        isActive: { eq: true }
        direction: {
          directionType: { in: $directions }
          isPromotion: { in: $isPromotion }
          categories: { in: $category }
        }
        newsDate: { gte: $startDate, lte: $endDate }
      }
    ) {
      nodes {
        newsDate
        title
        id
        slug
        direction {
          directionType
          content
          description
          slug
        }
      }
    }
    productBlockJson(slug: { eq: $productSlug }) {
      title
      description
      image {
        alt
        format
        src {
          childImageSharp {
            gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
          }
        }
      }
    }
  }
`;

export const Head = ({ data, pageContext }: HeadProps<Queries.NewsListQuery, PageContext>) => {
  const canonical = data?.site?.siteMetadata?.siteUrl as string | undefined;
  const meta = pageContext?.directions?.includes('PremiumUltra')
    ? premiumUltraMeta(canonical)
    : mainMeta(canonical);

  return <Meta yandexMetrikaId={projectSettings.YANDEX_METRIKA_ID} data={meta} />;
};
